import React, { useEffect, useState } from 'react';
import { ButtonToolbar, DropdownButton, MenuItem } from 'react-bootstrap';
import moment from 'moment';
import ContributionCalendar from './ContributionCalendar.jsx';
import {
  findImpactWithMinMaxDates,
  getYearsBetweenDates,
  HEATMAP_LEGEND,
} from './utils/index.js';
import ViewImpactsFormModal from './ViewImpactsFormModal/index.jsx';

const CalendarHeatmap = ({ results, query, project }) => {
  const [reportType, setReportType] = useState('scale');
  const [shownDataPeriod, setShownDataPeriod] = useState([]);
  const [isShownImpacts, setIsShownImpacts] = useState(false);
  const [shownImpacts, setShownImpacts] = useState(null);
  const [shownImpactsNumber, setShownImpactsNumber] = useState(0);
  const [maxNumberImpactsDay, setMaxNumberImpactsDay] = useState(0);

  const handleSelect = (e) => {
    setReportType(e);
  };

  const onHideModalImpacts = () => {
    setIsShownImpacts(false);
    setShownImpacts(null);
  };

  const getYearStartAndEndDates = (year) => {
    const startDate = new Date(year, 0, 1);
    const endDate = new Date(year, 11, 31);
    return { startDate, endDate };
  };

  useEffect(() => {
    const minAndMaxDatesImpact = findImpactWithMinMaxDates(results);
    if (results) {
      const currentDate = new Date();

      const currentYear = currentDate.getFullYear();
      const startOfCurrentYear = new Date(currentYear, 0, 1);

      const nextYear = currentYear + 1;
      const endOfNextYear = new Date(nextYear, 11, 31);

      const getStarAndEndDates = () => {
        const currentDate = new Date();

        const currentYear = currentDate.getFullYear();
        const startOfCurrentYear = new Date(currentYear, 0, 1);

        const nextYear = currentYear + 1;
        const endOfNextYear = new Date(nextYear, 11, 31);

        if (query.startDate && query.endDate) {
          return {
            startDate: query.startDate,
            endDate: query.endDate,
          };
        }

        if (
          !query.startDate &&
          !query.endDate &&
          !Object.values(query).some((val) => !!val)
        ) {
          return {
            startDate: minAndMaxDatesImpact.minStartDateImpact.startDate,
            endDate: endOfNextYear,
          };
        }

        if (
          !query.startDate &&
          !query.endDate &&
          Object.values(query).some((val) => !!val)
        ) {
          const queryDateStart = new Date(
            minAndMaxDatesImpact.minStartDateImpact.startDate
          );
          const queryYearStart = queryDateStart.getFullYear();
          const { startDate } = getYearStartAndEndDates(queryYearStart);

          const queryDateEnd = new Date(
            minAndMaxDatesImpact.maxEndDateImpact.endDate
          );
          const queryYearEnd = queryDateEnd.getFullYear();
          const { endDate } = getYearStartAndEndDates(queryYearEnd);

          return {
            startDate,
            endDate,
          };
        }

        if (query.startDate && !query.endDate) {
          if (
            new Date(query.startDate) >
            new Date(minAndMaxDatesImpact.maxEndDateImpact.endDate)
          ) {
            const queryDate = new Date(query.startDate);
            const queryYear = queryDate.getFullYear();
            const { startDate, endDate } = getYearStartAndEndDates(queryYear);
            return {
              startDate: query.startDate,
              endDate: moment(endDate).format('MM/DD/YYYY'),
            };
          }
          const queryDate = new Date(
            minAndMaxDatesImpact.maxEndDateImpact.endDate
          );
          const queryYear = queryDate.getFullYear();
          const { endDate } = getYearStartAndEndDates(queryYear);
          return {
            startDate: query.startDate,
            endDate,
          };
        }

        if (!query.startDate && query.endDate) {
          if (
            new Date(query.endDate) <
            new Date(minAndMaxDatesImpact.minStartDateImpact.startDate)
          ) {
            const queryDate = new Date(query.endDate);
            const queryYear = queryDate.getFullYear();
            const { startDate } = getYearStartAndEndDates(queryYear);
            return {
              startDate: moment(startDate).format('MM/DD/YYYY'),
              endDate: query.endDate,
            };
          }
          const queryDateStart = new Date(
            minAndMaxDatesImpact.minStartDateImpact.startDate
          );
          const queryYearStart = queryDateStart.getFullYear();
          const { startDate } = getYearStartAndEndDates(queryYearStart);
          return {
            startDate,
            endDate: query.endDate,
          };
        }
      };

      const searchDates = getStarAndEndDates();

      const resultSetYears = getYearsBetweenDates(
        searchDates.startDate,
        searchDates.endDate
      );
      setShownDataPeriod(resultSetYears);
      setShownImpactsNumber(results.length);
      setMaxNumberImpactsDay(0);
    }
  }, [results]);

  return (
    <React.Fragment>
      <div
        className="component component-sm flex flex-1 flex-justify"
        style={{ padding: '12px 24px 12px 32px', maxHeight: '60px' }}
      >
        <div>
          <h2>Heat Map</h2>
        </div>
        <div>
          <div className="flex">
            <h5 className="m-r-16 text-muted">Group by</h5>
            <ButtonToolbar>
              <DropdownButton
                bsStyle="info"
                title={`CI ${HEATMAP_LEGEND[reportType].name}`}
                style={{ minWidth: '160px' }}
                id="status-filter"
                className="btn-block text-capitalize"
                onSelect={handleSelect}
              >
                <MenuItem active={reportType === 'scale'} eventKey="scale">
                  CI Scale
                </MenuItem>
                <MenuItem
                  active={reportType === 'perception'}
                  eventKey="perception"
                >
                  CI Perception
                </MenuItem>
              </DropdownButton>
            </ButtonToolbar>
          </div>
        </div>
      </div>
      {results.length ? (
        <div className="component component-bg-white">
          {shownDataPeriod.map((year, index) => (
            <ContributionCalendar
              query={query}
              isLastShownYear={index === shownDataPeriod.length - 1}
              isFirstShownYear={index === 0}
              key={`${year} ${index}`}
              data={results}
              reportType={reportType}
              project={project}
              shownDataPeriod={year}
              setIsShownImpacts={setIsShownImpacts}
              setShownImpacts={setShownImpacts}
              shownImpactsNumber={shownImpactsNumber}
              setShownImpactsNumber={setShownImpactsNumber}
              maxNumberImpactsDay={maxNumberImpactsDay}
              setMaxNumberImpactsDay={setMaxNumberImpactsDay}
            />
          ))}
          {shownImpacts ? (
            <ViewImpactsFormModal
              onHide={onHideModalImpacts}
              show={isShownImpacts}
              impacts={shownImpacts.impacts}
              date={shownImpacts.date}
              project={project}
            />
          ) : null}
          <div className="m-b-30 flex flex-align-center">
            <h4 className="sentiment m-r-12">
              {HEATMAP_LEGEND[reportType].title}:
            </h4>
            <div className="flex">
              {HEATMAP_LEGEND[reportType].valuesLegend.map((val, index) => (
                <div className="flex m-r-30" key={val}>
                  <div className="m-r-8">
                    <h5 className="text-muted m-b-0">{val}</h5>
                  </div>
                  <div className="flex">
                    {HEATMAP_LEGEND[reportType].colorsLegend[index].map(
                      (color) => (
                        <div
                          key={color + index}
                          style={{
                            width: '12px',
                            height: '12px',
                            background: color,
                          }}
                        />
                      )
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="component-bg-white component-border-bottom">
          <div className="center-block text-center m-b-36 m-t-36">
            <h2
              className="text-primary m-b-22"
              style={{ fontSize: '24px', fontWeight: '600' }}
            >
              Why Don't I See Any Impacts?
            </h2>
            <p className="text-muted m-b-24">
              Impacts matching the selected filter(s) do not have dates
              associated with them and will not appear on a Heat Map.
              <br /> Change Impacts require a date to appear on the Heat Map.
            </p>
          </div>
        </div>
      )}
    </React.Fragment>
  );
};

export default CalendarHeatmap;
